import { useEffect, useState } from 'react'
import { onValue, ref } from 'firebase/database'
import { db } from '../lib/firebase.js'

// Renders an in-app banner on the admin side when (a) the admin is
// signed in via PIN and (b) the RTDB has /config/demoBanner.enabled=true.
// Designed to no-op on the PBCC build (no demoBanner in that DB) and
// light up on the white-label tourneylive.web.app deploy.
//
// Dismissible — once closed, stays gone for the rest of the session.

const ADMIN_KEY = 'adminAuthenticated'
const DISMISS_KEY = 'demoBannerDismissed'

function readFlag(key) {
  // localStorage-first so the persistent admin flag is seen; sessionStorage
  // fallback keeps the per-session demoBannerDismissed flag working.
  try { return localStorage.getItem(key) === 'true' || sessionStorage.getItem(key) === 'true' } catch { return false }
}

export default function DemoAdminBanner() {
  const [cfg, setCfg] = useState(null)
  const [adminApproved, setAdminApproved] = useState(() => readFlag(ADMIN_KEY))
  const [dismissed, setDismissed] = useState(() => readFlag(DISMISS_KEY))

  useEffect(() => {
    let off
    try {
      off = onValue(
        ref(db, 'config/demoBanner'),
        (snap) => setCfg(snap.val() || null),
        () => setCfg(null),
      )
    } catch { setCfg(null) }
    return () => { try { off?.() } catch {} }
  }, [])

  useEffect(() => {
    const sync = () => {
      setAdminApproved(readFlag(ADMIN_KEY))
      setDismissed(readFlag(DISMISS_KEY))
    }
    window.addEventListener('authchange', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('authchange', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  if (!adminApproved) return null
  if (!cfg || cfg.enabled !== true) return null
  if (dismissed) return null

  const close = () => {
    try { sessionStorage.setItem(DISMISS_KEY, 'true') } catch {}
    setDismissed(true)
  }

  const title = cfg.title || 'Welcome to TourneyLive Demo'
  const body = cfg.body || 'This is a fully functional demo. Test all features — your club version will have your logo, colors, and course.'

  return (
    <div
      style={{
        position: 'fixed',
        left: 12, right: 12, bottom: 12,
        zIndex: 1001,
        padding: '14px 16px',
        background: 'linear-gradient(135deg, #0E2B5C 0%, #1B4D8A 100%)',
        color: '#ffffff',
        borderRadius: 14,
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        border: '1px solid #D4A017',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
      <div style={{
        fontSize: 22, lineHeight: '24px',
        flexShrink: 0,
      }}>👋</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 900, fontSize: 14,
          color: '#D4A017', letterSpacing: '0.04em',
          marginBottom: 4, textTransform: 'uppercase',
        }}>
          {title}
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.92)' }}>
          {body}
        </div>
      </div>
      <button
        onClick={close}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.3)',
          color: '#ffffff',
          width: 28, height: 28, borderRadius: 14,
          cursor: 'pointer',
          fontSize: 14, fontWeight: 800,
          padding: 0,
        }}>
        ✕
      </button>
    </div>
  )
}
